<script setup lang="ts">
import { AbcTable, type AbcTableHeaderCell, type AbcTableRow } from "@ordbogen-npm/abc-dsk";
import { capitalize } from "lodash-es";
import { computed } from "vue";

import InflectionSchemeCell from "@/components/article/dictsys/inflectionSchemes/InflectionSchemeCell.vue";
import { buildComparisonLabel } from "@/components/article/dictsys/inflectionSchemes/labels";
import { mapGrammaticalLabelsToLevel } from "@/components/article/dictsys/level-mapping";
import { useSettingsStore } from "@/stores";
import type { DictsysInflectionSchemeGermanAdjectiveComparisons } from "@/types";

const props = defineProps<{
  comparisonScheme: DictsysInflectionSchemeGermanAdjectiveComparisons;
}>();

const settingsStore = useSettingsStore();

const comparisonHeaders = {
  danish: "Gradbøjning",
  latin: "Komparation"
};

const comparisonHeader = computed<AbcTableHeaderCell<string>>(() => ({
  span: 2,
  value: mapGrammaticalLabelsToLevel(
    settingsStore.activeWordClassLevel,
    comparisonHeaders.danish,
    comparisonHeaders.latin
  )
}));

const superlative = computed<string>(() => {
  const { superlative } = props.comparisonScheme;

  // Inspired by/ported PHP code from HNI
  if (!superlative || !superlative[0]) {
    return "-";
  }

  let result = "";

  // Grab the first article (prefix), if any
  if (superlative[0].prefix) {
    result = superlative[0].prefix.trim() + " ";
  }

  // Append first inflection
  result += superlative[0].inflexion.trim();

  // Append first suffix, if any
  if (superlative[0].suffix) {
    result += " " + superlative[0].suffix.trim();
  }

  // Loop remaining inflections. Grouping der/die/das together
  const superlativeArray = Object.values(superlative);
  superlativeArray.shift();
  for (const comparison of superlativeArray) {
    if (!comparison) {
      continue;
    }

    // der/die/das are always the last elements, in that order, and we want them displayed together
    let prefix;
    if (comparison.prefix.trim() === "der") {
      prefix = "der/die/das";
    } else {
      prefix = comparison.prefix.trim();
    }
    // Since der/die/das are always in that order and last, we can safely stop looping at "die"
    if (prefix === "die") {
      return result;
    }
    result += ", ";
    if (prefix) {
      result += prefix + " ";
    }
    result += comparison.inflexion.trim();
    if (comparison.suffix) {
      result += " " + comparison.suffix.trim();
    }
  }

  return result;
});

const rows = computed<AbcTableRow<string>[]>(() => {
  const { positive, comparative } = props.comparisonScheme;

  const rows = [
    {
      cells: [comparisonLabel("positive"), positive[0].inflexion]
    }
  ];

  if (comparative && comparative[0]) {
    rows.push({
      cells: [comparisonLabel("comparative"), comparative[0].inflexion]
    });
  }

  rows.push({
    cells: [comparisonLabel("superlative"), superlative.value]
  });

  return rows;
});

const comparisonLabel = (
  comparison: keyof DictsysInflectionSchemeGermanAdjectiveComparisons
): string => capitalize(buildComparisonLabel(settingsStore.activeWordClassLevel, comparison));
</script>

<template>
  <AbcTable :columns="[comparisonHeader]" :rows="rows">
    <template #body-cell="{ cell }">
      <InflectionSchemeCell :content="cell" />
    </template>
  </AbcTable>
</template>
